import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { validateSignUp } from '@wlb/shared/utils/validation';
import { clearStaleUserSessionIfUnauthenticated } from '@wlb/shared/api/auth/clearUserSession';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import {
  AVAILABLE_SOCIAL_PROVIDERS,
  socialProviderLabelKey,
} from '../features/auth/socialProviders';
import { useSocialAuth } from '../features/auth/useSocialAuth';
import { GoogleSignInButton } from '../features/auth/GoogleSignInButton';
import { SocialProviderIcon } from '../features/auth/SocialProviderIcon';

export function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // SIGNUP_INIT stores the token OnboardingRoute checks, so the account exists
  // but onboarding is still incomplete — the same state mobile lands in.
  const onSocialSignUp = useCallback(() => navigate('/basic-info'), [navigate]);
  const social = useSocialAuth({ mode: 'signup', onSuccess: onSocialSignUp });

  useEffect(() => {
    clearStaleUserSessionIfUnauthenticated().catch(() => undefined);
  }, []);

  const handleContinue = (event: React.FormEvent) => {
    event.preventDefault();
    const validationErrors = validateSignUp({ email });
    const emailError = validationErrors.email;
    if (emailError) {
      setError(t(emailError));
      return;
    }
    setError('');
    navigate('/signup/profile', { state: { email: email.trim() } });
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('auth.signUp.heading')}</h1>
        <p className="auth-flow__subheading">{t('auth.signUp.subheading')}</p>

        <form className="auth-flow__form" onSubmit={handleContinue}>
          <label className="auth-flow__field">
            <span className="sr-only">{t('auth.signUp.emailPlaceholder')}</span>
            <input
              className={`auth-flow__input${error ? ' auth-flow__input--error' : ''}`}
              type="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder={t('auth.signUp.emailPlaceholder')}
              autoComplete="email"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'signup-email-error' : undefined}
              autoFocus
            />
            <FormError message={error} id="signup-email-error" />
          </label>
          <button className="auth-flow__primary" type="submit" disabled={social.pending !== null}>
            {t('common.buttons.continue')}
          </button>

          {AVAILABLE_SOCIAL_PROVIDERS.length > 0 && (
            <>
              <div className="auth-flow__divider">{t('common.orContinueWith')}</div>
              <FormError variant="banner" message={social.error} id="signup-social-error" />
              {AVAILABLE_SOCIAL_PROVIDERS.map(provider =>
                provider === 'google' ? (
                  <GoogleSignInButton
                    key={provider}
                    disabled={social.pending !== null}
                    onResult={result => social.complete('google', result)}
                  />
                ) : (
                  <button
                    key={provider}
                    className="auth-flow__social"
                    type="button"
                    onClick={() => social.start(provider)}
                    disabled={social.pending !== null}
                  >
                    <SocialProviderIcon provider={provider} />
                    {social.pending === provider
                      ? t('common.loading')
                      : t(socialProviderLabelKey(provider))}
                  </button>
                ),
              )}
            </>
          )}
        </form>

        <p className="auth-flow__footer">
          {t('auth.common.alreadyHaveAccount')}
          <Link to="/login">{t('common.buttons.logIn')}</Link>
        </p>
        <p className="auth-flow__privacy">{t('common.privacyPolicy')}</p>
      </div>
    </AuthFlowLayout>
  );
}
